<template>
  <div class="uber-trip-wrap">
    <div class="filter-outerblk">
      <div class="single-fieldsouter search-field">
        <v-text-field
          v-model="filter.searchquery"
          :label="searchLabel"
          color="#ff9600"
          outlined
          dense
          hide-details
          clearable
        ></v-text-field>
      </div>

      <!-- No Branch filter here: the Branch selector in the top bar already
           scopes every request through the `branch` header. -->
      <div class="single-fieldsouter">
        <v-autocomplete
          v-model="filter.tripType"
          :items="tripTypeOptions"
          label="Trip Type"
          item-text="name"
          item-value="id"
          color="#ff9600"
          outlined
          dense
          hide-details
          clearable
          @change="getTripListing"
        ></v-autocomplete>
      </div>

      <div class="single-fieldsouter" v-if="statusOptions.length > 1">
        <v-autocomplete
          v-model="filter.status"
          :items="statusOptions"
          label="Status"
          item-text="label"
          item-value="value"
          color="#ff9600"
          outlined
          dense
          hide-details
          clearable
          @change="getTripListing"
        ></v-autocomplete>
      </div>

      <div class="single-fieldsouter">
        <v-menu
          v-model="dateModal"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="dateLabel"
              label="Date Range"
              readonly
              v-on="on"
              outlined
              dense
              hide-details
            ></v-text-field>
          </template>
          <v-date-picker v-model="filter.dateRange" range color="#d59e1d" no-title @change="onDateChange"></v-date-picker>
        </v-menu>
      </div>

      <div class="reset-outer">
        <v-btn class="lighter-btn" color="#ff9600" @click="getTripListing" text>
          <i class="fas fa-redo"></i>
        </v-btn>
      </div>
      <div class="reset-outer">
        <v-btn class="reset-btn" color="#ff9500" small @click="reset">Reset</v-btn>
      </div>
    </div>
    <div class="table-outerblk">
      <v-overlay absolute :value="isLoading" :opacity="0.7" color="#ffffff" z-index="4">
        <v-progress-circular indeterminate :size="50" :width="5" color="#d59e1d"></v-progress-circular>
      </v-overlay>

      <v-simple-table fixed-header v-if="trips.length > 0">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Trip ID</th>
              <th class="text-left">Crew Details</th>
              <th class="text-left">Pickup Address</th>
              <th class="text-left">Drop Address</th>
              <th class="text-left">Pickup Time</th>
              <th class="text-left">Reporting Time</th>
              <th class="text-left">Driver / Vehicle</th>
              <th class="text-left">Status</th>
              <!-- Only the two live tabs can act on a trip; Completed and
                   Cancelled keep their eight columns. -->
              <th class="text-left" v-if="showActions">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="trip in trips"
              :key="trip.tripId"
              class="clickable-row"
              :class="{ 'critical-row': isCritical(trip.uberStatus) }"
              @click="openDetails(trip)"
            >
              <td>
                <div class="stack-cell">
                  <span class="strong-text">{{ trip.displayId || trip.tripId }}</span>
                  <span class="muted-text">{{ tripTypeName(trip.tripType) }}</span>
                </div>
              </td>
              <td>
                <CrewDetails
                  :employees="trip.employees"
                  :fallbackName="guestName(trip)"
                  :fallbackMobile="guestMobile(trip)"
                />
              </td>
              <td class="address-cell">{{ addressOf(trip.pickup) }}</td>
              <td class="address-cell">{{ addressOf(trip.dropoff) }}</td>
              <td>{{ formatDate(trip.pickupTime) }}</td>
              <td>{{ formatDate(trip.reportingTime) }}</td>
              <td>
                <div class="stack-cell" v-if="showDriverDetails(trip) && trip.driver && trip.driver.name">
                  <span class="strong-text">{{ trip.driver.name }}</span>
                  <span class="muted-text">{{ vehicleText(trip) }}</span>
                </div>
                <span v-else>--</span>
              </td>
              <td>
                <span class="status-chip" :style="{ background: statusColor(trip.uberStatus) }">
                  {{ statusLabel(trip.uberStatus) }}
                </span>
              </td>
              <td v-if="showActions">
                <!-- Compact icon action, as the corporate portal's trip rows do
                     it: a filled button on every row competes with the trip data
                     it sits beside. @click.stop so cancelling does not also open
                     the drawer. -->
                <v-tooltip bottom v-if="isCancellable(trip.uberStatus)">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn
                        icon
                        class="uber-icon-btn cancel"
                        :loading="cancellingId === trip.tripId"
                        :disabled="cancellingId !== null"
                        @click.stop="confirmCancel(trip)"
                      >
                        <v-icon size="17">mdi-close-circle</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span>Cancel Trip</span>
                </v-tooltip>
                <!-- in_progress and anything terminal: Uber will not cancel it. -->
                <span class="muted-text" v-else>--</span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div class="empty-state" v-else-if="!isLoading">
        <i class="fas fa-taxi"></i>
        <span>{{ emptyMessage }}</span>
      </div>
    </div>

    <div class="pagination" v-if="rowCount > 0">
      <span class="count-text">{{ rowCount }} trips</span>
      <v-pagination
        v-model="page"
        :length="pageLength"
        :total-visible="6"
        :disabled="isLoading"
        @input="fetchTrips"
      ></v-pagination>
    </div>

    <!-- The details drawer is hosted by UberTrips, outside the tab panel. -->

    <Confirm ref="confirm"></Confirm>
  </div>
</template>

<script>
import axios from "@/axios/axios-global";
import moment from "moment";
import toast from "@/components/common/toast";
import Confirm from "./UberConfirm.vue";
import CrewDetails from "./CrewDetails.vue";
import uberRealtime from "@/services/uberRealtime";
import {
  TRIP_TYPE_OPTIONS,
  STATUS_GROUP,
  statusesForGroup,
  statusLabel,
  statusColor,
  isCritical,
  isCancellable,
  shouldShowDriverDetails,
} from "@/config/uberStatus";

// Tabs whose trips still have a live booking, so cancelling makes sense.
const ACTION_TABS = ["scheduled", "ongoing"];

const GROUP_BY_TYPE = {
  ongoing: STATUS_GROUP.ONGOING,
  scheduled: STATUS_GROUP.SCHEDULED,
  attention: STATUS_GROUP.ATTENTION,
  completed: STATUS_GROUP.COMPLETED,
  cancelled: STATUS_GROUP.CANCELLED,
};

const EMPTY_MESSAGE = {
  ongoing: "No ongoing trips.",
  scheduled: "No scheduled or reserved trips.",
  attention: "Nothing needs attention. All trips have a driver.",
  completed: "No completed trips for this period.",
  cancelled: "No cancelled trips for this period.",
};

export default {
  name: "UberTripList",
  components: { Confirm, CrewDetails },
  props: {
    listType: {
      type: String,
      required: true,
    },
    focusTrip: {
      type: [Number, String],
      default: null,
    },
    // Bumped by the shell after a cancel, so the list re-fetches without the
    // shell having to reach in through $refs.
    refreshSignal: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      filter: this.defaultFilter(),
      tripTypeOptions: TRIP_TYPE_OPTIONS,
      trips: [],
      isLoading: false,
      page: 1,
      pageSize: 25,
      rowCount: 0,
      currentTrip: 0,
      dateModal: false,
      searchTimer: null,
      unsubscribe: null,
      // Trip id being cancelled, so only that row spins.
      cancellingId: null,
    };
  },
  computed: {
    pageLength() {
      return this.pageSize > 0 ? Math.ceil(this.rowCount / this.pageSize) : 1;
    },
    showActions() {
      return ACTION_TABS.includes(this.listType);
    },
    statusOptions() {
      return statusesForGroup(GROUP_BY_TYPE[this.listType] || STATUS_GROUP.ONGOING).map((value) => ({
        value,
        label: statusLabel(value),
      }));
    },
    searchLabel() {
      return "Trip ID / Guest Name / Mobile / Driver / Vehicle No";
    },
    emptyMessage() {
      return EMPTY_MESSAGE[this.listType] || "No data available";
    },
    dateLabel() {
      const range = this.filter.dateRange || [];
      if (range.length === 0) return "";
      if (range.length === 1) return moment(range[0]).format("DD MMM YYYY");
      const [from, to] = [...range].sort();
      return `${moment(from).format("DD MMM")} - ${moment(to).format("DD MMM YYYY")}`;
    },
  },
  watch: {
    "filter.searchquery"(val) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (!val || val.length === 0 || val.length >= 3) this.getTripListing();
      }, 400);
    },
    listType() {
      this.filter = this.defaultFilter();
      this.getTripListing();
    },
    focusTrip(tripId) {
      if (tripId) this.focusOn(tripId);
    },
    refreshSignal() {
      this.fetchTrips();
    },
  },
  mounted() {
    this.getTripListing();

    this.unsubscribe = uberRealtime.subscribe({
      update: this.onRealtimeUpdate,
    });
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    statusLabel,
    statusColor,
    isCritical,
    isCancellable,
    shouldShowDriverDetails,

    defaultFilter() {
      // Completed and cancelled default to the last 7 days; live tabs are
      // unbounded because a scheduled trip can sit weeks out.
      const historic = ["completed", "cancelled"].includes(this.listType);
      return {
        searchquery: "",
        tripType: null,
        status: null,
        dateRange: historic
          ? [moment().subtract(7, "days").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")]
          : [],
      };
    },

    payload(page) {
      const range = [...(this.filter.dateRange || [])].sort();
      return {
        // The server resolves listType to its own status group, so the tabs
        // cannot drift from the statuses the webhook writes.
        listType: this.listType,
        status: this.filter.status || null,
        search: this.filter.searchquery || "",
        tripType: this.filter.tripType,
        startDate: range[0] || null,
        endDate: range[1] || range[0] || null,
        page,
        limit: this.pageSize,
      };
    },

    /**
     * The toAssign row shape is mapped into one internal shape here, so the
     * list never deals with API field names. bigId is the numeric tripid the
     * Uber endpoints expect; uberDetails carries the booking summary.
     */
    mapRow(row) {
      const uber = row.uberDetails || {};
      return {
        tripId: row.bigId,
        displayId: row.id,
        name: row.user,
        mobile: row.mobile,
        branch: row.branch,
        source: row.source,
        destination: row.destination,
        // The time the ride was booked with Uber for, falling back to the trip's
        // own. They are the same for a normal booking, but a re-book or an
        // on-demand dispatch can move it, and the Uber value is the one the
        // details drawer and the crew's ride page both show.
        pickupTime: uber.pickupTime || row.pickUpTime,
        reportingTime: row.reportingTime,
        // Crew rows for the details cell: name, mobile, crewId, flightInfo.
        employees: row.employees || [],
        serviceType: row.serviceType,
        tripType: uber.mode || null,
        uberStatus: uber.uberStatus || null,
        uberTripId: uber.uberTripId || null,
        guest: uber.guest || null,
        // The booking record may carry no addresses (a trip booked elsewhere,
        // or before uberDetails existed), so fall back to the trip's own.
        pickup: uber.pickup || (row.source ? { address: row.source } : null),
        dropoff: uber.dropoff || (row.destination ? { address: row.destination } : null),
        driver: uber.driver || null,
        vehicle: uber.vehicle || null,
        reBooking: row.reBooking || false,
      };
    },

    /** Fetches the current page. Pagination replaces rows rather than appending. */
    async fetchTrips() {
      this.isLoading = true;
      try {
        const response = await axios.post("uber/trip/booked", this.payload(this.page));
        const data = response.data || {};
        this.trips = (data.list || []).map(this.mapRow);

        // The server only counts on page 1 - it costs as much as the page
        // itself. Later pages omit it, so keep the total we already have.
        if (data.total !== null && data.total !== undefined) {
          this.rowCount = data.total;
          this.$emit("count", this.rowCount);
        }

        if (this.focusTrip && this.trips.some((trip) => trip.tripId === this.focusTrip)) {
          this.currentTrip = this.focusTrip;
        } else {
          this.currentTrip = this.trips.length > 0 ? this.trips[0].tripId : 0;
        }
      } catch (e) {
        this.trips = [];
        this.rowCount = 0;
        this.currentTrip = 0;
      } finally {
        this.isLoading = false;
      }
    },

    /** Any filter change resets to the first page. */
    getTripListing() {
      this.page = 1;
      return this.fetchTrips();
    },

    /**
     * A status change either patches a row in place, or moves the trip out of
     * this tab entirely. Rather than re-fetching on every event, patch what we
     * can and drop rows that no longer belong to this group.
     */
    onRealtimeUpdate(update) {
      const index = this.trips.findIndex((trip) => trip.tripId === update.tripId);
      const belongs = statusesForGroup(GROUP_BY_TYPE[this.listType] || STATUS_GROUP.ONGOING).includes(
        update.uberStatus
      );

      if (index === -1) {
        // New arrival into this tab - pull it in on the next list refresh.
        if (belongs) this.getTripListing();
        return;
      }

      if (!belongs) {
        this.trips.splice(index, 1);
        this.rowCount = Math.max(0, this.rowCount - 1);
        this.$emit("count", this.rowCount);
        if (this.currentTrip === update.tripId) {
          this.currentTrip = this.trips.length > 0 ? this.trips[0].tripId : 0;
        }
        return;
      }

      this.$set(this.trips, index, { ...this.trips[index], ...update });
    },

    focusOn(tripId) {
      const found = this.trips.find((trip) => trip.tripId === tripId);
      if (found) {
        this.currentTrip = tripId;
      } else {
        this.getTripListing();
      }
    },

    openDetails(trip) {
      this.currentTrip = trip.tripId;
      this.$emit("open-details", trip.tripId);
    },

    vehicleText(trip) {
      const vehicle = trip.vehicle || {};
      const name = `${vehicle.make || ""} ${vehicle.model || ""}`.trim();
      const plate = vehicle.licensePlate || vehicle.vehicleno || "";
      return [plate, name].filter(Boolean).join(" · ") || "--";
    },

    showDriverDetails(trip) {
      return shouldShowDriverDetails(trip.uberStatus);
    },

    /** Cancels the Uber booking for one row. */
    async confirmCancel(trip) {
      const confirmed = await this.$refs.confirm.open(
        "Confirm Uber Ride Cancellation",
        "Are you sure you want to cancel this Uber trip?",
        { icon: "fas fa-times-circle", color: "#000000" }
      );
      if (!confirmed) return;

      this.cancellingId = trip.tripId;
      try {
        const response = await axios.post("uber/trip/cancel", { tripId: trip.tripId });
        toast.success((response.data && response.data.message) || "Trip cancelled.");
        // The trip is now on the Cancelled tab, so it leaves this one. Re-fetch
        // rather than splicing so the count and the page stay right.
        this.getTripListing();
        this.$emit("cancelled", trip.tripId);
      } catch (err) {
        const data = err && err.response && err.response.data;
        toast.error(
          (data && data.message) ||
            (typeof data === "string" && data) ||
            "Failed to cancel the trip."
        );
      } finally {
        this.cancellingId = null;
      }
    },

    onDateChange() {
      if ((this.filter.dateRange || []).length === 2) {
        this.dateModal = false;
        this.getTripListing();
      }
    },

    reset() {
      this.filter = this.defaultFilter();
      this.getTripListing();
    },

    guestName(trip) {
      if (trip.guest) {
        return `${trip.guest.firstName || ""} ${trip.guest.lastName || ""}`.trim() || "--";
      }
      return trip.name || "--";
    },

    guestMobile(trip) {
      return (trip.guest && trip.guest.phoneNumber) || trip.mobile || "--";
    },

    addressOf(place) {
      if (!place) return "--";
      return place.address || "--";
    },

    tripTypeName(id) {
      const found = TRIP_TYPE_OPTIONS.find((option) => option.id === id);
      return found ? found.name : id;
    },

    /** pickUpTime arrives as an epoch value; moment would try to ISO-parse it. */
    formatDate(value) {
      if (value === null || value === undefined || value === "") return "--";
      const parsed =
        typeof value === "number" || /^\d+$/.test(String(value))
          ? moment(Number(value))
          : moment(value);
      return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : "--";
    },
  },
};
</script>

<style lang="scss" scoped>
// Fills whatever height the tab shell gives it - no assumption about the
// surrounding chrome.
.uber-trip-wrap {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  // Measured and published by UberTrips; the calc is only for the first paint.
  height: var(--uber-tab-height, calc(100vh - 190px));
  box-sizing: border-box;
  overflow: hidden;
}

.filter-outerblk {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  flex-shrink: 0;
}

.single-fieldsouter {
  display: flex;
  align-items: center;
  flex: 1 1 190px;
  max-width: 230px;
}

.search-field {
  flex: 1 1 300px;
  max-width: 340px;
}

.reset-outer {
  display: flex;
  align-items: center;
}

.reset-btn {
  font-weight: 600;
  letter-spacing: 0.2px;
}

.table-outerblk {
  margin-top: 14px;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  overflow: hidden;
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  ::v-deep .v-data-table {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  ::v-deep .v-data-table__wrapper {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  th {
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6a6a6a !important;
    white-space: nowrap;
  }

  td {
    font-size: 13px;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.clickable-row {
  cursor: pointer;
}

.critical-row {
  background: #fff8f8;
}

.stack-cell {
  display: flex;
  flex-direction: column;
}

.strong-text {
  font-weight: 600;
  color: #333333;
}

.muted-text {
  font-size: 11px;
  color: #8a8a8a;
}

.address-cell {
  max-width: 220px;
  white-space: normal;
}

// Same treatment as the Re-book button on the Attention tab.
// Row action button, matching .uber-icon-btn in the corporate portal's trip
// rows. ::v-deep because Vuetify owns the inner button element.
::v-deep .uber-icon-btn {
  width: 28px !important;
  height: 28px !important;
  border-radius: 7px !important;
  transition: transform 0.12s ease, box-shadow 0.12s ease !important;

  &:hover {
    transform: translateY(-1px) !important;
  }

  &.cancel {
    background: linear-gradient(135deg, #c62828 0%, #ef5350 100%) !important;
    box-shadow: 0 2px 6px rgba(198, 40, 40, 0.4) !important;

    .v-icon {
      color: #ffffff !important;
    }

    &:hover {
      background: linear-gradient(135deg, #b71c1c 0%, #e53935 100%) !important;
      box-shadow: 0 3px 10px rgba(198, 40, 40, 0.55) !important;
    }
  }

  // Vuetify greys the icon but leaves the gradient, which reads as still
  // clickable while another row's cancel is in flight.
  &.v-btn--disabled {
    opacity: 0.45;
  }
}

.status-chip {
  padding: 2px 9px;
  border-radius: 10px;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  color: #b0b0b0;
  font-size: 14px;

  i {
    font-size: 32px;
  }
}

// Same white card as the To be Booked and Attention Required tabs. The compact
// stacked variant this replaces was for the narrow list panel, which is gone.
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  // Lets .count-text pin itself left while the pager stays centred.
  position: relative;
  width: 100%;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(106, 7, 75, 0.15);
  margin-top: 14px;
  padding: 6px 16px;
  // The tab body is a flex column; without this the bar is squeezed when the
  // table is full.
  flex-shrink: 0;
}

.count-text {
  position: absolute;
  left: 16px;
  font-size: 12px;
  color: #8a8a8a;
}
</style>
